import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BlogPostsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function BlogPostsSubTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    telegram_post_url: '',
    order_index: 0,
    is_active: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/blog-posts`);
      setPosts(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке статей: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/blog-posts`, formData);
      setShowCreateForm(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      setError('Ошибка при создании статьи: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API_URL}/api/blog-posts/${id}`, formData);
      setEditingPost(null);
      resetForm();
      fetchPosts();
    } catch (err) {
      setError('Ошибка при обновлении статьи: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту статью?')) {
      try {
        await axios.delete(`${API_URL}/api/blog-posts/${id}`);
        fetchPosts();
      } catch (err) {
        setError('Ошибка при удалении статьи: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post.id);
    setFormData({
      category: post.category,
      title: post.title,
      description: post.description,
      telegram_post_url: post.telegram_post_url,
      order_index: post.order_index,
      is_active: post.is_active
    });
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      category: '',
      title: '',
      description: '',
      telegram_post_url: '',
      order_index: 0,
      is_active: true
    });
    setEditingPost(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingPost(null);
    resetForm();
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="blog-posts-sub-tab">
      <div className="tab-header">
        <h2>Статьи блога</h2>
        <button className="btn btn-primary" onClick={() => {
          resetForm();
          setShowCreateForm(true);
          setEditingPost(null);
        }}>
          + Создать статью
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(showCreateForm || editingPost) && (
        <div className="blog-post-form">
          <h3>{editingPost ? 'Редактировать статью' : 'Создать статью'}</h3>
          <div className="form-group">
            <label>Категория:</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="Например: Карьера"
              maxLength={255}
            />
          </div>
          <div className="form-group">
            <label>Название:</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Название статьи"
              maxLength={255}
            />
          </div>
          <div className="form-group">
            <label>Описание:</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Описание статьи"
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>Ссылка на пост в Telegram:</label>
            <input
              type="text"
              value={formData.telegram_post_url}
              onChange={(e) => setFormData({ ...formData, telegram_post_url: e.target.value })}
              placeholder="https://t.me/channel/123"
              maxLength={500}
            />
          </div>
          <div className="form-group">
            <label>Порядок отображения:</label>
            <input
              type="number"
              value={formData.order_index}
              onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
              placeholder="0 (автоматически)"
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Активна
            </label>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={editingPost ? () => handleUpdate(editingPost) : handleCreate}>
              {editingPost ? 'Сохранить' : 'Создать'}
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Отмена
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Категория</th>
              <th>Название</th>
              <th>Описание</th>
              <th>Ссылка</th>
              <th>Порядок</th>
              <th>Активна</th>
              <th>Дата создания</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state">
                  Нет статей. Создайте первую статью.
                </td>
              </tr>
            ) : (
              posts.map((post) => (
                <tr key={post.id}>
                  <td>{post.id}</td>
                  <td>{post.category}</td>
                  <td className="blog-post-title">{post.title}</td>
                  <td className="blog-post-description">{post.description}</td>
                  <td className="blog-post-url">
                    <a href={post.telegram_post_url} target="_blank" rel="noopener noreferrer">
                      {post.telegram_post_url.length > 30 ? post.telegram_post_url.substring(0, 30) + '...' : post.telegram_post_url}
                    </a>
                  </td>
                  <td>{post.order_index}</td>
                  <td>{post.is_active ? 'Да' : 'Нет'}</td>
                  <td>{new Date(post.created_at).toLocaleString('ru-RU')}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(post)}
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(post.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BlogPostsSubTab;
